import { useState, useEffect, useCallback, useRef } from "react";
import { getApiErrorMessage } from "../utils/consoleUtils";

/**
 * Fetch data for console views with cancellation and consistent loading/error state.
 * @param {() => Promise<any>} fetcher - Async function that returns data (no args).
 * @param {Array} deps - Dependency array for when to re-run (e.g. [cityId]).
 * @param {{ refetchOnWindowFocus?: boolean }} options - Optional.
 * @returns {{ data: any, loading: boolean, error: string | null, refetch: () => Promise<void> }}
 */
export function useConsoleFetch(fetcher, deps = [], options = {}) {
  const { refetchOnWindowFocus = true } = options;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const runFetch = useCallback(() => {
    setLoading(true);
    setError(null);
    return fetcherRef
      .current()
      .then((result) => {
        setData(result);
        setError(null);
      })
      .catch((e) => {
        setError(getApiErrorMessage(e));
        setData(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcherRef
      .current()
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(getApiErrorMessage(e));
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, deps);

  useEffect(() => {
    if (!refetchOnWindowFocus) return;
    const onFocus = () => runFetch();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refetchOnWindowFocus, runFetch]);

  const refetch = useCallback(() => runFetch(), [runFetch]);

  return { data, loading, error, refetch };
}
